import { useEffect, useState } from 'react';
import axios from "axios"
import Cookies from "js-cookie"
import { useAlert } from "../../../../../constants/AlertProvider"
import { SkeletonLoader } from '../../../../common/SkeletonLoader';
import { SearchBar } from './SearchBar';
import { MapPin, UserIcon, MessageCircleMore } from 'lucide-react';
import { Pagination } from '@mui/material';

export function TalentList() {
    const [candidates, setCandidates] = useState([]);
    const [totalCandidates, setTotalCandidates] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const candidatesPerPage = 10;
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const showAlert = useAlert();

    const getInitials = (name) => {
        if (!name) return "";
        const nameParts = name.split(' ').filter(Boolean);
        if (nameParts.length === 0) return "";
        const firstInitial = nameParts[0][0].toUpperCase();
        const lastInitial = nameParts.length > 1 ? nameParts[nameParts.length - 1][0].toUpperCase() : '';
        return lastInitial ? `${firstInitial}.${lastInitial}` : firstInitial;
    };

    const handleSearch = (query) => {
        // When a new search is performed, reset to the first page
        if (query !== searchQuery) {
            setCurrentPage(1);
        }
        setSearchQuery(query);
    };

    // Normalize data from search results to match the component's expected structure
    const normalizeSearchResults = (results) => {
        return results.map(result => ({
            id: result.id,
            name: result.name,
            profile_picture: result.profile_picture,
            address_details: {
                formatted_address: result.address?.formatted_address || "Location not set"
            },
            application: {
                // The search result has skills directly, let's adapt it
                skills: (result.skills || []).map(skill => ({
                    id: skill.name, // Use name as key since search result has no skill ID
                    name: skill.name
                }))
            }
        }));
    };

    useEffect(() => {
        const fetchTalent = async () => {
            setLoading(true);
            const offset = (currentPage - 1) * candidatesPerPage;
            try {
                const csrftoken = Cookies.get("csrftoken");

                if (searchQuery) {
                    // --- SEARCH LOGIC ---
                    const searchResponse = await axios.get("/api/search/candidates/", {
                        params: { q: searchQuery, limit: candidatesPerPage, offset },
                        headers: { "X-CSRFToken": csrftoken }
                    });
                    const normalizedResults = normalizeSearchResults(searchResponse.data.results);
                    console.log("Search API Res: ", normalizedResults);
                    setCandidates(normalizedResults);
                    setTotalCandidates(searchResponse.data.total_count);
                    setTotalPages(Math.ceil(searchResponse.data.total_count / candidatesPerPage));
                } else {
                    // --- DEFAULT FETCH LOGIC ---
                    const usersResponse = await axios.get("/api/users/", {
                        params: { role: "candidate", is_looking_for_job: true, limit: candidatesPerPage, offset },
                        headers: {
                            "X-CSRFToken": csrftoken,
                            "Content-Type": "application/json"
                        }
                    });
                    setTotalCandidates(usersResponse.data.count);
                    setTotalPages(Math.ceil(usersResponse.data.count / candidatesPerPage));

                    const candidatesData = usersResponse.data.results;

                    const candidatesWithDetails = await Promise.all(
                        candidatesData.map(async (candidate) => {
                            let fullCandidateData = { ...candidate, application: { skills: [] }, address_details: null };
                            try {
                                // Step 1: Fetch candidate profile for address, name, applicationids etc.
                                const profileResponse = await axios.get(`/api/candidate-profile/${candidate.id}/`, {
                                    headers: { "X-CSRFToken": csrftoken }
                                });
                                fullCandidateData = { ...fullCandidateData, ...profileResponse.data };

                                // Step 2: Use application ID to fetch skills
                                if (profileResponse.data.applicationids && profileResponse.data.applicationids.length > 0) {
                                    const appId = profileResponse.data.applicationids[0].id;
                                    const appResponse = await axios.get(`/api/application/${appId}/`, {
                                        headers: { "X-CSRFToken": csrftoken }
                                    });
                                    // Merge skills into the candidate data
                                    fullCandidateData.application = appResponse.data;
                                }

                                return fullCandidateData;
                            } catch (error) {
                                console.error(`Error fetching full profile for candidate ${candidate.id}:`, error);
                                return fullCandidateData; // Return data with fallbacks
                            }
                        })
                    );
                    setCandidates(candidatesWithDetails);
                }

            } catch (error) {
                showAlert("Error fetching candidates", "error");
                console.error("Error fetching candidates:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchTalent();
    }, [showAlert, searchQuery, currentPage]);

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    };

    return (
        <section className="mt-8 flex flex-col gap-x-6">
            <SearchBar onSearch={handleSearch} />
            <div className="mt-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-y-4">
                <p className="text-sm font-normal text-gray-900 dark:text-gray-100">{totalCandidates} Profiles found</p>
                {/* <div className="flex flex-row gap-x-2 items-center">
                    <label htmlFor="sortby" className="text-sm font-normal text-gray-900">Sort by:</label>
                    <select id="sortby" defaultValue="relevance" className="p-2 border-2 border-gray-300 rounded-md text-sm font-semibold text-gray-900 focus:outline-none focus:border-blue-500 bg-white">
                        <option value="relevance">Relevance</option>
                        <option value="experience">Experience</option>
                        <option value="match">Match Score</option>
                    </select>
                </div> */}
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
                {loading ? (
                    Array.from({ length: 4 }).map((_, index) => (
                        <div key={index} className="rounded-lg shadow-sm bg-white dark:bg-gray-800 p-6">
                            <SkeletonLoader className="h-40" />
                        </div>
                    ))
                ) : (candidates.map((candidate) => (
                    <div key={candidate.id} className="rounded-lg shadow-sm bg-white dark:bg-gray-800 flex flex-col">
                        <div className='p-6 flex flex-col sm:flex-row items-center sm:items-start gap-4'>
                            <div className="flex flex-col items-center sm:items-start flex-grow">
                                <h3 className="text-gray-900 dark:text-gray-100 font-medium text-lg mb-1 text-center sm:text-left">{getInitials(candidate.name)}</h3>
                                <div className="flex flex-wrap gap-2 mb-4 justify-center sm:justify-start">
                                    {candidate.application && candidate.application.skills && candidate.application.skills.length > 0 ? (
                                        candidate.application.skills.slice(0, 5).map((skill) => (
                                            <span
                                                key={skill.id}
                                                className="bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-medium px-2 py-0.5 rounded-lg"
                                            >
                                                {skill.name}
                                            </span>
                                        ))
                                    ) : (
                                        <span className="text-gray-500 dark:text-gray-400 text-xs font-medium">N/A</span>
                                    )}
                                </div>
                                <div className='flex gap-x-2'>
                                    <div className="text-gray-500 dark:text-gray-400 text-sm flex items-center mb-4">
                                        <MapPin className="h-4 w-4 mr-1" />
                                        <span>{candidate.address_details?.formatted_address || "Location not set"}</span>
                                    </div>
                                    <p className="text-gray-500 dark:text-gray-400 text-sm">•</p>
                                    <div className="text-gray-500 dark:text-gray-400 text-sm flex items-center mb-4">
                                        <span>{candidate.application.description || "NA"}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex-shrink-0">
                                {candidate.profile_picture ? (
                                    <img
                                        src={candidate.profile_picture}
                                        alt={candidate.name}
                                        className="size-14 rounded-full"
                                    />
                                ) : (
                                    <div className="size-14 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center text-blue-700 dark:text-blue-300 font-bold text-lg">
                                        <UserIcon />
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="w-full border-t border-gray-200 dark:border-gray-700 grid grid-cols-2">
                            <a
                                href={`/candidate/${candidate.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-4 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-semibold text-gray-900 dark:text-gray-100 cursor-pointer transition rounded-bl-lg text-center"
                            >
                                View Profile
                            </a>
                            <button
                                className="p-4 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-semibold text-blue-700 dark:text-blue-400 flex flex-row gap-x-2 items-center justify-center cursor-pointer transition rounded-br-lg"
                            >
                                <MessageCircleMore className='w-5 h-5' />
                                <span>Message</span>
                            </button>
                        </div>
                    </div>
                )))}
            </div>
            <div className="mt-8 flex justify-center">
                <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                />
            </div>
        </section>
    );
}